/**
 * Fase 3 — Prøvesett steg 5: Spørsmål.
 *
 * Hoppes over hvis primary_assessment_type !== 'written'.
 * Hvis spørsmålsbank: viser bank-status + knapp for å generere flere spørsmål.
 * Hvis statisk: viser liste over spørsmål med add/remove.
 *
 * Fase 7 legger til bildestøtte her.
 */
import { useEffect, useState, useCallback } from 'react';
import { Database, Loader2, Sparkles, AlertTriangle, CheckCircle2, Plus, Info } from 'lucide-react';
import {
  previewQuestionBank,
  validateQuestionBank,
  generateQuestionsToBank,
} from '../../../lib/examApi';
import type { QuestionBankPreview, QuestionBankValidation } from '../../../types/exam';
import type { ExamSetWizardData } from '../NewExamSetWizard';
import { useToast } from '../../ui';

export interface QuestionsStepProps {
  data: ExamSetWizardData;
  patch: (updates: Partial<ExamSetWizardData>) => void;
}

export default function QuestionsStep({ data, patch }: QuestionsStepProps) {
  const toast = useToast();
  const [preview, setPreview] = useState<QuestionBankPreview | null>(null);
  const [validation, setValidation] = useState<QuestionBankValidation | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const refreshPreview = useCallback(async () => {
    if (!data.use_question_bank || !data.study_plan_id) return;
    setLoading(true);
    try {
      const [prev, val] = await Promise.all([
        previewQuestionBank(data.study_plan_id, data.question_count, data.difficulty_distribution),
        validateQuestionBank(data.study_plan_id, data.question_count),
      ]);
      setPreview(prev);
      setValidation(val);
    } catch {
      setPreview(null);
      setValidation(null);
    } finally {
      setLoading(false);
    }
  }, [data.use_question_bank, data.study_plan_id, data.question_count, data.difficulty_distribution]);

  useEffect(() => {
    refreshPreview();
  }, [refreshPreview]);

  const handleGenerateToBank = async () => {
    if (!data.study_plan_id) return;
    setGenerating(true);
    try {
      const result = await generateQuestionsToBank(data.study_plan_id, {
        questionsPerLiterature: 5,
        difficultyDistribution: data.difficulty_distribution,
        minimumQuestions: data.question_count,
      });
      const generated = result.totalQuestionsGenerated || result.generated || 0;
      toast.showSuccess(`${generated} nye spørsmål lagt til spørsmålsbanken. Gjennomgå og godkjenn dem før publisering.`);
      await refreshPreview();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Ukjent feil';
      toast.showError('Generering feilet: ' + msg);
    } finally {
      setGenerating(false);
    }
  };

  // Hvis ikke-skriftlig: skipp
  if (data.primary_assessment_type !== 'written') {
    return (
      <div className="rounded-lg bg-purple-50 border border-purple-200 p-4 text-sm text-purple-900">
        <p className="font-semibold mb-1">Dette steget hoppes over</p>
        <p>
          Planlagt vurderingsform er <strong>{data.primary_assessment_type}</strong>. Sensor registrerer vurdering direkte
          — ingen spørsmål trengs. Trykk Neste.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {data.use_question_bank ? (
        <>
          {/* Bank-overlay */}
          <div className="rounded-lg bg-gradient-to-br from-purple-50 to-white border border-purple-200 p-4">
            <div className="flex items-start gap-3">
              <Database className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-kf-gray-900">Spørsmålsbank aktiv</h4>
                <p className="text-sm text-kf-gray-700 mt-1">
                  Hver kurs-instans får et randomisert utvalg på <strong>{data.question_count} spørsmål</strong> fra banken,
                  med fordeling{' '}
                  <strong>{Math.round(data.difficulty_distribution.easy * 100)}% enkel ·{' '}
                  {Math.round(data.difficulty_distribution.medium * 100)}% middels ·{' '}
                  {Math.round(data.difficulty_distribution.hard * 100)}% vanskelig</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* Bank-status */}
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-kf-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              Sjekker spørsmålsbank…
            </div>
          ) : validation && preview ? (
            <div
              className={`rounded-lg p-4 border ${
                validation.valid
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-amber-50 border-amber-200'
              }`}
            >
              <div className="flex items-start gap-3">
                {validation.valid ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <h4
                    className={`font-semibold ${
                      validation.valid ? 'text-emerald-900' : 'text-amber-900'
                    }`}
                  >
                    {validation.valid
                      ? 'Spørsmålsbanken er klar'
                      : 'Spørsmålsbanken trenger flere spørsmål'}
                  </h4>
                  <p className="text-sm text-kf-gray-700 mt-1">
                    Godkjente spørsmål: <strong>{preview.available?.total || 0}</strong> av{' '}
                    <strong>{data.question_count}</strong> påkrevd.
                  </p>
                  <p className="text-xs text-kf-gray-500 mt-1">
                    Enkel: {preview.available?.byDifficulty?.easy || 0} · Middels:{' '}
                    {preview.available?.byDifficulty?.medium || 0} · Vanskelig:{' '}
                    {preview.available?.byDifficulty?.hard || 0}
                  </p>
                  {validation.issues && validation.issues.length > 0 && (
                    <ul className="mt-2 text-sm text-amber-800 list-disc list-inside">
                      {validation.issues.map((issue, idx) => (
                        <li key={idx}>{issue.message}</li>
                      ))}
                    </ul>
                  )}
                  <button
                    type="button"
                    onClick={handleGenerateToBank}
                    disabled={generating}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg disabled:opacity-50"
                  >
                    {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {generating ? 'Genererer…' : 'Generer flere med KI'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-md bg-kf-gray-50 border border-kf-gray-200 px-3 py-2 text-sm text-kf-gray-600">
              Kan ikke sjekke spørsmålsbank — kontroller at studieplan er valgt.
            </div>
          )}

          <div className="rounded-md bg-kf-blue-50 border border-kf-blue-200 p-3 text-xs text-kf-blue-900 flex items-start gap-2">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <strong>Smart randomisering per kurs:</strong> Fra Fase 8 vil alle kandidater i samme kursgjennomføring få
              nøyaktig de samme spørsmålene (snapshot). Mellom kurs trekkes nye sett. Dette hindrer fasit-deling.
            </div>
          </div>
        </>
      ) : (
        // Statisk-modus (ikke anbefalt, men støttet)
        <StaticQuestionsEditor data={data} patch={patch} />
      )}
    </div>
  );
}

interface StaticQuestionsEditorProps {
  data: ExamSetWizardData;
  patch: (updates: Partial<ExamSetWizardData>) => void;
}

function StaticQuestionsEditor({ data, patch }: StaticQuestionsEditorProps) {
  const addEmpty = () => {
    patch({
      questions: [
        ...data.questions,
        {
          question_type: 'multiple_choice',
          question_text: '',
          points: 1,
          options: [
            { text: '', is_correct: true },
            { text: '', is_correct: false },
            { text: '', is_correct: false },
            { text: '', is_correct: false },
          ],
        },
      ],
    });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <strong>Statisk spørsmålssett valgt.</strong> Alle kandidater får samme spørsmål. Vi anbefaler spørsmålsbank for
          bedre kvalitetssikring (bytt i Teknisk-steget hvis ønsket).
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-kf-gray-900">
          Spørsmål ({data.questions.length})
        </h4>
        <button
          type="button"
          onClick={addEmpty}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-kf-blue-700 hover:bg-kf-blue-50 rounded border border-kf-blue-200"
        >
          <Plus className="w-4 h-4" />
          Legg til spørsmål
        </button>
      </div>

      {data.questions.length === 0 ? (
        <div className="text-sm text-kf-gray-500 py-4 text-center">
          Ingen spørsmål lagt til ennå. Klikk "Legg til spørsmål" eller bytt til spørsmålsbank.
        </div>
      ) : (
        <div className="space-y-3">
          {data.questions.map((q, idx) => (
            <div key={idx} className="rounded-lg border border-kf-gray-200 p-3 text-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Spørsmål {idx + 1}</span>
                <button
                  type="button"
                  onClick={() => patch({ questions: data.questions.filter((_, i) => i !== idx) })}
                  className="text-red-600 hover:text-red-800 text-xs"
                >
                  Fjern
                </button>
              </div>
              <textarea
                value={q.question_text}
                onChange={(e) => {
                  const updated = [...data.questions];
                  updated[idx] = { ...updated[idx], question_text: e.target.value };
                  patch({ questions: updated });
                }}
                rows={2}
                placeholder="Spørsmålstekst…"
                className="w-full px-2 py-1 border border-kf-gray-300 rounded"
              />
              <p className="text-xs text-kf-gray-500 mt-1">
                Full redigering av svaralternativer og bilder kommer i spørsmål-editoren (Fase 3+7). I denne versjonen
                anbefaler vi spørsmålsbank-modus.
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
